import React, { useEffect, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Radio, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import {
  actualizarFactura, actualizarGasto, crearFacturaEgreso, crearGastoEgreso, enlazarSolicitudEgreso, fmtFecha, fmtMoney, getSolicitudesParaEnlazar,
} from "../services/egresosService";
import { fieldSx } from "./formStyles";

const hoy = () => new Date().toISOString().slice(0, 10);
const msg = (e, def) => e?.response?.data?.error || def;
const fechaCorta = (v) => (v ? String(v).slice(0, 10) : "");

// Alta o edición de una factura del comprobante (guarda_factura.php / editar_factura.php).
export function FacturaDialog({ abierto, egresoId, factura, onClose, onGuardado }) {
  const vacia = { fecha_factura: hoy(), nit: "", denominacion: "", nro_factura: "", nro_autorizacion: "", importe_total: "", importe_no_sujeto: "", cod_control: "" };
  const [f, setF] = useState(vacia);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setError("");
    setF(factura ? { ...vacia, ...Object.fromEntries(Object.keys(vacia).map((k) => [k, k === "fecha_factura" ? fechaCorta(factura[k]) : String(factura[k] ?? "")])) } : vacia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, factura]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      if (factura) await actualizarFactura(factura.id, f); else await crearFacturaEgreso(egresoId, f);
      onGuardado(factura ? "Factura actualizada." : "Factura registrada.");
      onClose();
    } catch (e) {
      setError(msg(e, "No se pudo guardar la factura."));
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, label, size = 6, extra = {}) => (
    <Grid size={{ xs: 12, sm: size }}><TextField fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx} {...extra} /></Grid>
  );
  return (
    <Dialog open={abierto} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>{factura ? "Editar factura" : "Registrar factura"}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        <Grid container spacing={0.75}>
          {campo("fecha_factura", "Fecha de factura", 6, { type: "date", slotProps: { inputLabel: { shrink: true } } })}
          {campo("nit", "NIT del proveedor")}
          {campo("denominacion", "Nombre / denominación", 12)}
          {campo("nro_factura", "N° de factura")}
          {campo("nro_autorizacion", "N° de autorización")}
          {campo("importe_total", "Importe total", 4)}
          {campo("importe_no_sujeto", "Importe sujeto a CF", 4)}
          {campo("cod_control", "Código de control", 4)}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose}>Cerrar</Button>
        <Button size="small" variant="contained" disabled={guardando} onClick={guardar}>{guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Alta o edición de otro gasto del comprobante (guarda_gasto.php / editar_gasto.php).
export function GastoDialog({ abierto, egresoId, gasto, onClose, onGuardado }) {
  const [g, setG] = useState({ fecha: hoy(), monto: "", detalle: "" });
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setError("");
    setG(gasto ? { fecha: fechaCorta(gasto.fecha), monto: String(gasto.monto ?? ""), detalle: gasto.detalle ?? "" } : { fecha: hoy(), monto: "", detalle: "" });
  }, [abierto, gasto]);

  const set = (k) => (e) => setG((p) => ({ ...p, [k]: e.target.value }));
  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      if (gasto) await actualizarGasto(gasto.id, g); else await crearGastoEgreso(egresoId, g);
      onGuardado(gasto ? "Gasto actualizado." : "Gasto registrado.");
      onClose();
    } catch (e) {
      setError(msg(e, "No se pudo guardar el gasto."));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={abierto} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>{gasto ? "Editar gasto" : "Registrar gasto"}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        <Grid container spacing={0.75}>
          <Grid size={6}><TextField fullWidth size="small" type="date" label="Fecha" value={g.fecha} onChange={set("fecha")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} /></Grid>
          <Grid size={6}><TextField fullWidth size="small" label="Monto Bs" value={g.monto} onChange={set("monto")} sx={fieldSx} /></Grid>
          <Grid size={12}><TextField fullWidth multiline minRows={2} size="small" label="Detalle" value={g.detalle} onChange={set("detalle")} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], py: "3px" } }} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose}>Cerrar</Button>
        <Button size="small" variant="contained" disabled={guardando} onClick={guardar}>{guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Enlaza el cheque con una solicitud pendiente o aprobada (enlazar.php); la solicitud pasa a "emitido".
export function EnlazarSolicitudDialog({ abierto, egresoId, onClose, onGuardado }) {
  const [filas, setFilas] = useState(null);
  const [sel, setSel] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setFilas(null); setSel(null); setError("");
    getSolicitudesParaEnlazar().then((r) => setFilas(r.data || [])).catch((e) => setError(msg(e, "No se pudo cargar las solicitudes.")));
  }, [abierto]);

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await enlazarSolicitudEgreso(egresoId, sel);
      onGuardado("Comprobante enlazado con la solicitud.");
      onClose();
    } catch (e) {
      setError(msg(e, "No se pudo enlazar."));
    } finally {
      setGuardando(false);
    }
  };

  const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };
  return (
    <Dialog open={abierto} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Enlazar con solicitud</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {!filas && !error && <CircularProgress size={18} />}
        {filas && (
          <Table size="small">
            <TableHead>
              <TableRow>{["", "ID", "Fecha", "Dirigido a", "Concepto", "Servicio", "Monto Bs", "Estado", "Solicitante"].map((h) => <TableCell key={h} sx={{ ...cellSx, fontWeight: 800 }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {!filas.length ? <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">No hay solicitudes pendientes ni aprobadas.</Typography></TableCell></TableRow> : filas.map((s) => (
                <TableRow key={s.id} hover onClick={() => setSel(s.id)} sx={{ cursor: "pointer" }}>
                  <TableCell padding="checkbox"><Radio size="small" checked={sel === s.id} sx={{ p: 0.3 }} /></TableCell>
                  <TableCell sx={cellSx}>{s.id}</TableCell><TableCell sx={cellSx}>{fmtFecha(s.fecha)}</TableCell><TableCell sx={cellSx}>{s.nombre}</TableCell>
                  <TableCell sx={cellSx}>{s.concepto}</TableCell><TableCell sx={cellSx}>{s.servicio}</TableCell>
                  <TableCell sx={cellSx} align="right">{fmtMoney(s.bolivianos)}</TableCell><TableCell sx={cellSx}>{s.estado}</TableCell><TableCell sx={cellSx}>{s.usu}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose}>Cerrar</Button>
        <Button size="small" variant="contained" disabled={!sel || guardando} onClick={guardar}>Enlazar</Button>
      </DialogActions>
    </Dialog>
  );
}
